"use client";
import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { useSignin } from "../../../hooks/use-signin";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";

// Supabase handled inside useSignin hook.

export default function SigninPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const signin = useSignin();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    signin.mutate(
      { email, password },
      {
        onSuccess: () => {
          router.replace("/dashboard");
        },
        onError: (err: unknown) => {
          const msg =
            typeof err === "object" && err && "message" in err
              ? (err as { message?: string }).message || "Sign in failed"
              : "Sign in failed";
          setError(msg);
          setLoading(false);
        },
      }
    );
  }

  return (
    <div className="relative mx-auto max-w-xl min-h-screen pt-28 md:pt-36 px-4">
      <div className="rounded-xl border bg-card text-card-foreground shadow-lg backdrop-blur-sm">
        <div className="border-b px-6 py-4">
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome Back
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Sign in to access your dashboard, update listings and manage claims.
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="px-6 py-7 grid gap-6"
          aria-describedby={error ? "signin-error" : undefined}
        >
          <div className="grid gap-2">
            <label className="text-sm font-medium">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium">Password</label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="pr-14"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute top-1/2 right-2 -translate-y-1/2 px-2 text-xs"
              >
                {showPassword ? "Hide" : "Show"}
              </Button>
            </div>
          </div>
          {error && (
            <p
              id="signin-error"
              aria-live="polite"
              className="text-sm text-destructive"
            >
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading || signin.isPending}
            className="w-full font-medium"
          >
            {loading || signin.isPending ? "Signing in..." : "Sign In"}
          </Button>
          <p className="text-xs text-muted-foreground">
            Forgot password?{" "}
            <a href="/reset" className="underline">
              Reset it
            </a>
            .
          </p>
        </form>
        <div className="border-t px-6 py-5 text-sm text-muted-foreground">
          Need an account?{" "}
          <a className="underline" href="/signup">
            Create one
          </a>
        </div>
      </div>
    </div>
  );
}
